import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef } from 'react';

import {
  type ImpersonationStoreState,
  useImpersonationSelector,
} from '@revelio/auth';
import { ImpersonationTrackingEvents } from '@revelio/core';

import {
  describeImpersonation,
  IMPERSONATION_SUPER_PROPERTY_KEYS,
  type ImpersonationDescription,
  impersonationStartedProperties,
  impersonationSuperProperties,
} from '../adminRewrite/impersonation/impersonation-analytics';
import {
  consumeImpersonationSource,
  type ImpersonationSource,
} from '../adminRewrite/impersonation/impersonation-source';

const STARTED_AT_STORAGE_KEY = 'rl_impersonation_started_at';

const identityOf = (active: ImpersonationDescription) => JSON.stringify(active);

const registerSuperProperties = (active: ImpersonationDescription) =>
  mixpanel.register(impersonationSuperProperties(active));

const unregisterSuperProperties = () => {
  for (const key of IMPERSONATION_SUPER_PROPERTY_KEYS) mixpanel.unregister(key);
};

// sessionStorage, like the impersonation store itself, so a reload mid-session
// keeps the real start for `duration_s`. Where storage is blocked the duration
// counts from the page load instead — a floor, never an overstatement.
const readStartedAt = (): number | null => {
  try {
    const raw = sessionStorage.getItem(STARTED_AT_STORAGE_KEY);
    const startedAt = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(startedAt) ? startedAt : null;
  } catch {
    return null;
  }
};

const writeStartedAt = (startedAt: number | null) => {
  try {
    if (startedAt === null) {
      sessionStorage.removeItem(STARTED_AT_STORAGE_KEY);
    } else {
      sessionStorage.setItem(STARTED_AT_STORAGE_KEY, String(startedAt));
    }
  } catch {
    // Storage blocked — the in-memory start still covers this page load.
  }
};

const selectState = (state: ImpersonationStoreState) => state;

/**
 * `impersonation started` / `impersonation ended`, plus the `is_impersonating`
 * super properties while one is active. Mixpanel keeps identifying the admin —
 * a client's board must never receive admin traffic under the client's name —
 * so the super properties are how every board excludes impersonated sessions.
 */
export const useImpersonationTracking = () => {
  const state = useImpersonationSelector(selectState);
  const active = useMemo(() => describeImpersonation(state), [state]);
  // `undefined` until the first effect: a page load is not a transition.
  const previousRef = useRef<ImpersonationDescription | null | undefined>(
    undefined
  );
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = active;

    const start = (
      next: ImpersonationDescription,
      source: ImpersonationSource
    ) => {
      const startedAt = Date.now();
      startedAtRef.current = startedAt;
      writeStartedAt(startedAt);
      registerSuperProperties(next);
      mixpanel.track(
        ImpersonationTrackingEvents.STARTED,
        impersonationStartedProperties(next, source)
      );
    };

    const end = (ended: ImpersonationDescription) => {
      const startedAt = startedAtRef.current ?? Date.now();
      startedAtRef.current = null;
      writeStartedAt(null);
      mixpanel.track(ImpersonationTrackingEvents.ENDED, {
        impersonation_mode: ended.mode,
        duration_s: Math.round((Date.now() - startedAt) / 1000),
      });
      unregisterSuperProperties();
    };

    if (previous === undefined) {
      // The store is sessionStorage-persisted, so an impersonation already
      // active on page load is a resumed one: keep its super properties without
      // counting a new start. With none active, drop whatever a tab closed
      // mid-impersonation left in Mixpanel's own storage, or the admin's next
      // plain session would still be stamped as impersonated.
      if (active) {
        startedAtRef.current = readStartedAt() ?? Date.now();
        registerSuperProperties(active);
      } else {
        unregisterSuperProperties();
      }
      return;
    }

    if (!previous && active) {
      start(active, consumeImpersonationSource());
    } else if (previous && !active) {
      end(previous);
    } else if (
      previous &&
      active &&
      identityOf(previous) !== identityOf(active)
    ) {
      // Switched who is being impersonated without stepping out first — the
      // modal replaces the state in one write. Close the old session, open the new.
      end(previous);
      start(active, consumeImpersonationSource());
    }
  }, [active]);
};
